// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  DeleteRounded,
  DownloadRounded,
  LaunchRounded,
} from '@/constants/hardfin-standard-icons';
import { File } from '@/file/api/types';
import { getDownloadURL } from '@/file/utilities/get-download-url';
import {
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { PropsWithChildren } from 'react';

interface Props {
  /**
   * A file to display in the table.
   */
  file: File;

  /**
   * If true, the row is disabled.
   */
  disabled?: boolean;

  /**
   * A callback fired when the delete button is pressed.
   */
  onDelete: () => void;
}

/**
 * A table row that displays a file's name, upload date, and actions.
 */
export function FileTableRow(props: PropsWithChildren<Props>) {
  const downloadURL = getDownloadURL(props.file);

  const uploadedAtTooltipDateTimeString = props.file.uploadedAt.toLocaleString({
    dateStyle: 'long',
    timeStyle: 'long',
  });

  return (
    <TableRow>
      <TableCell>
        <Typography variant="subtitle2">{props.file.name}</Typography>
      </TableCell>
      <TableCell>
        <Tooltip arrow title={`Uploaded on ${uploadedAtTooltipDateTimeString}`}>
          <Typography variant="body2">
            {props.file.uploadedAt.toLocaleString({ dateStyle: 'medium' })}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="end"
        >
          {props.file.url && (
            <IconButton
              href={props.file.url.toString()}
              size="small"
              target="_blank"
              rel="noopener"
              disabled={props.disabled}
              aria-label="Open"
            >
              <LaunchRounded fontSize="small" />
            </IconButton>
          )}
          {downloadURL && (
            <IconButton
              size="small"
              href={downloadURL.toString()}
              disabled={props.disabled}
              aria-label="Download"
            >
              <DownloadRounded fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={props.onDelete}
            disabled={props.disabled}
            aria-label="Delete"
          >
            <DeleteRounded fontSize="small" />
          </IconButton>
          {props.children}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
